/**
 * ACCOUNT-ADMINISTRATORS
 * Edit list of administrators for the accountRisksSettings
 */
import { useState } from "react"
import { store, useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { t } from "i18next"
import { faCopy, faCrown, faEdit, faEllipsisV, faUserCircle, faUserMinus } from "@fortawesome/free-solid-svg-icons"
import ListButton from "@/components/list-button"
import { User } from "@/redux/user.types"
import ListItem from "@/components/list-item"
import ListIcon from "@/components/list-icon"
import Space from "@/components/space"
import SendInvitationButton from "@/components/SendInvitationButton"
import copyLink from "@/utils/copy-link.utils"
import AdmSendEmailModal from "@/modals/adm-send-email.modal"
import AdmEditUserModal from "@/modals/adm-edit-user.modal"
import { toast } from "react-toastify"
import Card from "@/components/card"
import Chip from "@/components/chip"
import PageHeader from "@/components/page-header"
import Button from "@/components/button"
import { userGet, userUpdate } from "@/redux/user.actions"
import { adminsNavItem } from "@/nav/nav.app"
import { pick } from "lodash"
import PageLoader from "@/components/page-loader"

const ACTION_USER_COPY_LINK = "ACTION_USER_COPY_LINK"
const ACTION_USER_EDIT = "ACTION_USER_EDIT"
const ACTION_USER_INVITE = "ACTION_USER_INVITE"
const ACTION_USER_REMOVE = "ACTION_USER_REMOVE"

const MODAL_SEND_EMAIL = "MODAL_SEND_EMAIL"
const MODAL_USER_ADD = "MODAL_USER_ADD"
const MODAL_USER_EDIT = "MODAL_USER_EDIT"

export default function RiskAdmins() {
  const dispatch = useAppDispatch()

  //Selectors
  const _session = useAppSelector((state) => state._session)
  const users = useAppSelector((state) => state.user.list)
  const status = useAppSelector((state) => state.account.status)

  //State
  const [currentUser, setCurrentUser] = useState(new User())
  const [modal, setModal] = useState<null | string>(null)

  //Trigger specific action
  function clickAction(action: string, user: User) {
    const adminUser = new User({ ...user })
    setCurrentUser(adminUser)

    switch (action) {
      case ACTION_USER_COPY_LINK:
        return copyLink(new User(pick(user, ["id", "email", "firstname", "lastname"])), _session)
      case ACTION_USER_EDIT:
        return setModal(MODAL_USER_EDIT)
      case ACTION_USER_INVITE:
        return setModal(MODAL_SEND_EMAIL)
      case ACTION_USER_REMOVE:
        return save(adminUser, "remove")
      default:
        return null
    }
  }

  //Init actions
  function getActions() {
    const actions = [
      {
        id: ACTION_USER_EDIT,
        icon: faEdit,
        label: t("Modifier l'administrateur")
      }
    ]

    actions.push({
      id: ACTION_USER_COPY_LINK,
      icon: faCopy,
      label: t("Copier le lien d'invitation")
    })

    actions.push({
      id: ACTION_USER_REMOVE,
      icon: faUserMinus,
      label: t("Retirer de la liste des administrateurs")
    })
    return actions
  }

  //Open the modal email if allowed
  function openSendEmailModal(user: User) {
    setCurrentUser(user)
    setModal(user.email ? MODAL_SEND_EMAIL : null)
  }

  //Save changes
  async function save(user: User, operation: "add" | "edit" | "remove") {
    let nextUsers = users

    if (operation === "add") {
      if (users.some((u) => u.id === user.id)) {
        toast(t("Ce collaborateur est déjà rattaché."))
        return
      }

      const response: any = await store.dispatch(userUpdate({ ...user, role: "ADMIN", EstablishmentId: null }, true))
      if (response.error) return

      const savedUser = new User(response.id ? response : { ...user, role: "ADMIN", EstablishmentId: null })
      nextUsers = [...users, savedUser]
      toast(t("Administrateur ajouté au groupe."))
      setModal(null)
      openSendEmailModal(savedUser)
    }
    if (operation === "edit") {
      const response: any = await store.dispatch(userUpdate({ ...user, role: "ADMIN", EstablishmentId: null }, true))
      if (response.error) return

      const savedUser = new User(response.id ? response : { ...user, role: "ADMIN", EstablishmentId: null })
      nextUsers = users.map((u) => (u.id === savedUser.id ? savedUser : u))
      toast(t("Administrateur du groupe mis à jour."))
      setModal(null)
    }
    if (operation === "remove") {
      const response: any = await store.dispatch(userUpdate({ ...user, role: null, EstablishmentId: null }, true))
      if (response.error) return

      nextUsers = users.filter((u) => u.id !== user.id)
      toast(t("Administrateur retiré du groupe."))
    }

    dispatch(userGet(nextUsers))
  }

  return (
    <>
      {modal === MODAL_USER_ADD && (
        <AdmEditUserModal
          isSmall
          allowFromSameAccount
          onClose={() => setModal(null)}
          onNext={(user: User) => save(user, "add")}
        />
      )}

      {modal === MODAL_USER_EDIT && (
        <AdmEditUserModal
          isSmall
          userToUpdate={currentUser}
          onClose={() => setModal(null)}
          onNext={(user: User) => save(user, "edit")}
        />
      )}

      {modal === MODAL_SEND_EMAIL && (
        <AdmSendEmailModal
          emailCode="inviteOrganizationAdministrator"
          currentUser={currentUser}
          onClose={() => setModal(null)}
          textBold={`Souhaitez-vous envoyer un email d'invitation à ${currentUser.username} (${currentUser.email}) en tant qu'administrateur de l'organisation ${_session.accountName} ?`}
          items={[
            {
              id: "accountRisksSettings",
              value: _session.accountName
            }
          ]}
        />
      )}

      <PageHeader
        title={adminsNavItem.text}
        subtitle="Nommez les personnes responsables de la prévention des risques pour l'organisation.">
        <Button onClick={() => setModal(MODAL_USER_ADD)} className="primary">
          Ajouter un administrateur
        </Button>
      </PageHeader>

      <PageLoader status={status} forceLoading>
        <Card isWithoutPadding>
          {users.length === 0 ? (
            <ListItem className="grey-t font-bold">{t("Aucun administrateur")}</ListItem>
          ) : (
            users.map((user) => (
              <ListItem key={user.id}>
                <ListIcon image={user.imageUrl} fallbackIcon={faUserCircle} />
                {user.firstname} {user.lastname}
                <Space />
                {_session.userId === user.id && (
                  <Chip icon={faCrown} className="mr-4">
                    {t("utils_self")}
                  </Chip>
                )}
                {user.email ? (
                  <span className="grey-t">{user.email}</span>
                ) : (
                  <span className="orange-t">{t("invitation_no_email")}</span>
                )}
                <div className="w-10" />
                {user.id !== _session.userId && (
                  <>
                    <SendInvitationButton user={user} onClick={() => clickAction(ACTION_USER_INVITE, user)} />
                    <ListButton
                      onAction={(action: string) => clickAction(action, user)}
                      icon={faEllipsisV}
                      actions={getActions()}
                    />
                  </>
                )}
              </ListItem>
            ))
          )}
        </Card>
      </PageLoader>
    </>
  )
}
